import { oc } from "@orpc/contract";
import { z } from "zod";
import { Cents } from "./money";

/**
 * Revenue (spec, user story 50 and "Omzet"): what was paid or put on account, excl VAT, minus
 * refunds on the day of the refund, per Dutch calendar day and per product, split into consumer
 * and business, with shipping apart from the goods.
 */

/** A calendar date in Europe/Amsterdam. */
export const RevenueDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** The longest range one report covers: two years, so "this year" and a custom range both fit. */
export const MAX_REVENUE_DAYS = 731;

export const RevenueRangeInput = z.object({
	/** The first day, included. */
	from: RevenueDate,
	/** The last day, included. */
	to: RevenueDate,
});
export type RevenueRangeInput = z.infer<typeof RevenueRangeInput>;

export const SegmentRevenue = z.object({ goods: Cents, shipping: Cents });
export type SegmentRevenue = z.infer<typeof SegmentRevenue>;

/** Excl VAT; negative when refunds outweigh sales. */
export const RevenueTotals = z.object({
	consumer: SegmentRevenue,
	business: SegmentRevenue,
	/** Goods and shipping of both. */
	total: Cents,
});
export type RevenueTotals = z.infer<typeof RevenueTotals>;

export const DayRevenue = RevenueTotals.extend({ date: RevenueDate });
export type DayRevenue = z.infer<typeof DayRevenue>;

/** Goods only: shipping belongs to no product. */
export const ProductRevenue = z.object({
	productId: z.string(),
	/** The product's name now (as on the order when the product is gone). */
	name: z.string(),
	consumer: Cents,
	business: Cents,
	total: Cents,
});
export type ProductRevenue = z.infer<typeof ProductRevenue>;

export const RevenueReport = z.object({
	from: RevenueDate,
	to: RevenueDate,
	/** Every day of the range, in order, zero where nothing happened. */
	days: z.array(DayRevenue),
	/** Every product with a sale or refund in the range, highest total first. */
	products: z.array(ProductRevenue),
	total: RevenueTotals,
});
export type RevenueReport = z.infer<typeof RevenueReport>;

export const RevenuePeriod = z.object({ from: RevenueDate, to: RevenueDate, totals: RevenueTotals });
export type RevenuePeriod = z.infer<typeof RevenuePeriod>;

/** Today, this week (from Monday) and this month, up to and including today, in Dutch time. */
export const RevenueSummary = z.object({
	today: RevenuePeriod,
	week: RevenuePeriod,
	month: RevenuePeriod,
});
export type RevenueSummary = z.infer<typeof RevenueSummary>;

export const RevenueContract = {
	/**
	 * Revenue per day and per product over a range of Dutch calendar days, both ends included.
	 * Errors: `RevenueRangeInvalid` (400: not a date, `from` after `to`, or longer than two years).
	 */
	report: oc.route({ method: "GET", path: "/admin/revenue" }).input(RevenueRangeInput).output(RevenueReport),
	/** The start page's figures: today, this week and this month. */
	summary: oc.route({ method: "GET", path: "/admin/revenue/summary" }).output(RevenueSummary),
};
